"use client"

import React from "react"
import { Badge } from "@/components/ui/badge"
import {
  Bot,
  Users,
  Voicemail,
  Play,
  Clock,
  PhoneOff,
  ArrowRight,
  Check,
  X,
  PhoneIncoming,
} from "lucide-react"
import type { JourneyStep, JourneyStepType } from "@/types/unified-call"
import { cn } from "@/lib/utils"

interface JourneyBadgesProps {
  journey: JourneyStep[]
  compact?: boolean
  className?: string
}

const stepConfig: Record<JourneyStepType, {
  icon: React.ComponentType<{ className?: string }>
  label: string
  bgColor: string
  textColor: string
}> = {
  incoming_call: {
    icon: PhoneIncoming,
    label: "Incoming",
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
  },
  ai_agent: {
    icon: Bot,
    label: "AI",
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
  },
  operator: {
    icon: Users,
    label: "Operator",
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
  },
  voicemail: {
    icon: Voicemail,
    label: "Voicemail",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
  },
  play_message: {
    icon: Play,
    label: "Message",
    bgColor: "bg-teal-100",
    textColor: "text-teal-700",
  },
  time_check: {
    icon: Clock,
    label: "Time",
    bgColor: "bg-yellow-100",
    textColor: "text-yellow-700",
  },
  hangup: {
    icon: PhoneOff,
    label: "Hangup",
    bgColor: "bg-red-100",
    textColor: "text-red-700",
  },
  handover: {
    icon: ArrowRight,
    label: "Escalation",
    bgColor: "bg-indigo-100",
    textColor: "text-indigo-700",
  },
}

function getResultIcon(result?: string) {
  if (!result) return null
  if (result === "answered" || result === "completed") {
    return <Check className="w-2.5 h-2.5 text-green-600" />
  }
  if (result === "no_answer" || result === "busy" || result === "failed") {
    return <X className="w-2.5 h-2.5 text-red-500" />
  }
  return null
}

export function JourneyBadges({ journey, compact = true, className }: JourneyBadgesProps) {
  if (!journey || journey.length === 0) {
    return null
  }

  // Filter out incoming_call for compact display (it's implied)
  const displaySteps = compact
    ? journey.filter(step => step.type !== "incoming_call")
    : journey

  if (displaySteps.length === 0) {
    return null
  }

  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {displaySteps.map((step, index) => {
        const config = stepConfig[step.type] || stepConfig.incoming_call
        const Icon = config.icon
        const resultIcon = getResultIcon(step.result)

        return (
          <React.Fragment key={`${step.nodeId || step.type}-${index}`}>
            {index > 0 && (
              <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
            )}
            <Badge
              variant="outline"
              className={cn(
                "gap-0.5 px-1.5 py-0 text-xs font-normal",
                config.bgColor,
                config.textColor,
                "border-transparent"
              )}
            >
              <Icon className="w-3 h-3" />
              {!compact && <span className="ml-0.5">{config.label}</span>}
              {resultIcon && (
                <span className="ml-0.5">{resultIcon}</span>
              )}
            </Badge>
          </React.Fragment>
        )
      })}
    </div>
  )
}

interface JourneyBadgeProps {
  type: JourneyStepType
  result?: string
  showLabel?: boolean
  className?: string
}

export function JourneyBadge({ type, result, showLabel = false, className }: JourneyBadgeProps) {
  const config = stepConfig[type] || stepConfig.incoming_call
  const Icon = config.icon
  const resultIcon = getResultIcon(result)

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-0.5 px-1.5 py-0 text-xs font-normal",
        config.bgColor,
        config.textColor,
        "border-transparent",
        className
      )}
    >
      <Icon className="w-3 h-3" />
      {showLabel && <span className="ml-0.5">{config.label}</span>}
      {resultIcon && <span className="ml-0.5">{resultIcon}</span>}
    </Badge>
  )
}

export function CallTypeBadge({
  type,
  hasLinkedAiCall = false
}: {
  type: "voice_agent" | "pbx"
  hasLinkedAiCall?: boolean
}) {
  if (type === "voice_agent") {
    return (
      <Badge
        variant="outline"
        className="gap-1 bg-purple-50 text-purple-700 border-purple-200"
      >
        <Bot className="w-3 h-3" />
        <span>Voice Agent</span>
      </Badge>
    )
  }

  // PBX with linked AI call = combined view
  if (hasLinkedAiCall) {
    return (
      <Badge
        variant="outline"
        className="gap-1 bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 border-purple-200"
      >
        PBX + AI
      </Badge>
    )
  }

  return (
    <Badge
      variant="outline"
      className="gap-1 bg-blue-50 text-blue-700 border-blue-200"
    >
      <PhoneIncoming className="w-3 h-3" />
      <span>PBX</span>
    </Badge>
  )
}
